import { Checkbox as BaseCheckbox } from '@base-ui/react/checkbox';
import React, { useState } from 'react';

import { Icon } from '../../assets/Icon/Icon';
import {
  iconColor,
  raisedDisabledSelectedStyles,
  raisedDisabledStyles,
  raisedEnabledSelectedStyles,
  raisedEnabledStyles,
  selectors,
  transitions,
} from '../../controls/shared/styles';
import { styled } from '../../stitches.config';

const CheckboxInputCheckmark = styled(Icon, {
  width: '$10',
  height: '$10',
  color: iconColor,
  transition: transitions.control,
});
CheckboxInputCheckmark.displayName = 'CheckboxInputCheckmark';

const CheckboxInputContainer = styled(BaseCheckbox.Root, {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '$14',
  minWidth: '$14',
  height: '$14',
  minHeight: '$14',
  borderRadius: '$4',
  cursor: 'pointer',
  userSelect: 'none',
  transition: transitions.control,

  [selectors.focus]: {
    outline: 'none',
  },

  variants: {
    state: {
      initial: {},
      checked: {},
      indeterminate: {},
    },
    isDisabled: {
      true: {
        cursor: 'not-allowed',
        opacity: 0.5,
      },
      false: {},
    },
  },

  compoundVariants: [
    {
      state: 'initial',
      isDisabled: false,
      css: raisedEnabledStyles,
    },
    {
      state: 'checked',
      isDisabled: false,
      css: raisedEnabledSelectedStyles,
    },
    {
      state: 'indeterminate',
      isDisabled: false,
      css: { ...raisedEnabledStyles },
    },
    {
      state: 'initial',
      isDisabled: true,
      css: raisedDisabledStyles,
    },
    {
      state: 'checked',
      isDisabled: true,
      css: raisedDisabledSelectedStyles,
    },
    {
      state: 'indeterminate',
      isDisabled: true,
      css: raisedDisabledStyles,
    },
  ],
});
CheckboxInputContainer.displayName = 'CheckboxInputContainer';

export type CheckboxInputProps = {
  'aria-describedby'?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  checked?: boolean;
  defaultSelected?: boolean;
  disabled?: boolean;
  id?: string;
  indeterminate?: boolean;
  name?: string;
  onBlur?: React.FocusEventHandler<HTMLElement>;
  onChange?: (checked: boolean) => void;
  onClick?: React.MouseEventHandler<HTMLElement>;
  value?: string;
  ref?: React.Ref<HTMLInputElement | null>;
};

export function CheckboxInput({
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  checked,
  defaultSelected,
  disabled = false,
  id,
  indeterminate = false,
  name,
  onBlur,
  onChange,
  onClick,
  value,
  ref,
}: CheckboxInputProps) {
  const [uncontrolledChecked, setUncontrolledChecked] = useState(defaultSelected ?? false);
  const isChecked = checked ?? uncontrolledChecked;

  let state: 'checked' | 'indeterminate' | 'initial';
  if (isChecked) {
    state = 'checked';
  } else if (indeterminate) {
    state = 'indeterminate';
  } else {
    state = 'initial';
  }

  return (
    <CheckboxInputContainer
      state={state}
      isDisabled={disabled}
      aria-describedby={ariaDescribedBy}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      checked={isChecked}
      disabled={disabled}
      id={id}
      indeterminate={indeterminate}
      inputRef={ref}
      name={name}
      onBlur={onBlur}
      onCheckedChange={(nextChecked) => {
        if (checked === undefined) {
          setUncontrolledChecked(nextChecked);
        }
        onChange?.(nextChecked);
      }}
      onClick={onClick}
      value={value}
    >
      {state !== 'initial' && (
        <CheckboxInputCheckmark icon={state === 'checked' ? 'checkmark' : 'minus'} />
      )}
    </CheckboxInputContainer>
  );
}
